import { Link } from 'react-router-dom';
import { Scale, Briefcase, FileText, Users, Shield, TrendingUp, ArrowRight, GraduationCap } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';

const ServicesPage = () => {
  const mainServices = [
    {
      icon: Scale,
      title: 'Conseil Juridique',
      description: 'Expertise complète en droit des affaires pour sécuriser votre entreprise.',
      features: [
        'Rédaction et révision de contrats',
        'Conseil en droit des sociétés',
        'Protection de la propriété intellectuelle',
        'Gestion des litiges commerciaux',
        'Conformité réglementaire'
      ],
      color: 'bg-[#006F33]'
    },
    {
      icon: Briefcase,
      title: 'Création d\'Entreprise',
      description: 'Accompagnement complet pour lancer votre projet entrepreneurial.',
      features: [
        'Choix de la forme juridique',
        'Rédaction des statuts',
        'Immatriculation et formalités',
        'Ouverture de compte bancaire',
        'Obtention des licences nécessaires'
      ],
      color: 'bg-[#E6C200]',
      textDark: true
    },
    {
      icon: TrendingUp,
      title: 'Développement d\'Entreprise',
      description: 'Stratégies pour faire croître votre activité de manière durable.',
      features: [
        'Plan de développement stratégique',
        'Recherche de financement',
        'Expansion et partenariats',
        'Optimisation des processus',
        'Conseil en management'
      ],
      color: 'bg-[#C0392B]'
    }
  ];

  const additionalServices = [
    {
      icon: FileText,
      title: 'Formation Entrepreneuriale',
      description: 'Programmes de formation pour développer vos compétences en gestion d\'entreprise.'
    },
    {
      icon: Users,
      title: 'Mentorat & Coaching',
      description: 'Accompagnement personnalisé par nos experts pour guider vos décisions.'
    },
    {
      icon: Shield,
      title: 'Audit & Conformité',
      description: 'Vérification de la conformité juridique et recommandations d\'amélioration.'
    },
    {
      icon: GraduationCap,
      title: 'Études à l\'Étranger',
      description: 'Accompagnement complet pour poursuivre vos études en France ou au Canada.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Consultation Initiale',
      description: 'Nous analysons votre situation et vos objectifs lors d\'un premier entretien gratuit.'
    },
    {
      step: '02',
      title: 'Diagnostic Complet',
      description: 'Évaluation approfondie de vos besoins juridiques et entrepreneuriaux.'
    },
    {
      step: '03',
      title: 'Plan d\'Action',
      description: 'Élaboration d\'une stratégie personnalisée adaptée à votre projet.'
    },
    {
      step: '04',
      title: 'Accompagnement',
      description: 'Mise en œuvre du plan avec un suivi régulier de votre progression.'
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 md:py-32 gradient-hero" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <span className="text-sm font-semibold text-[#006F33] uppercase tracking-wider">Nos Services</span>
            <h1 className="hero-title mt-4" data-testid="services-title">
              Des solutions <span className="text-[#006F33]">sur mesure</span> pour votre entreprise
            </h1>
            <p className="hero-subtitle mt-6">
              Une expertise complète en conseil juridique et accompagnement entrepreneurial 
              pour transformer vos ambitions en réussites concrètes.
            </p>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 md:py-32 bg-white" data-testid="main-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {mainServices.map((service, index) => (
              <div 
                key={index}
                className={`${service.color} rounded-2xl p-8 ${service.textDark ? 'text-[#1A1A1A]' : 'text-white'}`}
                data-testid={`main-service-${index}`}
              >
                <div className={`w-14 h-14 rounded-xl ${service.textDark ? 'bg-black/10' : 'bg-white/20'} flex items-center justify-center mb-6`}>
                  <service.icon className="w-7 h-7" />
                </div>
                <h3 className="text-2xl font-bold mb-4">{service.title}</h3>
                <p className={`mb-6 ${service.textDark ? 'text-slate-700' : 'text-white/80'}`}>
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full ${service.textDark ? 'bg-[#006F33]' : 'bg-white'} mt-2`}></div>
                      <span className={service.textDark ? 'text-slate-600' : 'text-white/90'}>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 md:py-32 bg-[#F9F9F7]" data-testid="additional-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-[#006F33] uppercase tracking-wider">Services Complémentaires</span>
            <h2 className="section-title mt-4">Un accompagnement complet</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {additionalServices.map((service, index) => (
              <div key={index} className="service-card" data-testid={`additional-service-${index}`}>
                <div className="service-icon">
                  <service.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-[#1A1A1A]">{service.title}</h3>
                <p className="text-slate-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-[#006F33] uppercase tracking-wider">Notre Processus</span>
            <h2 className="section-title mt-4">Comment nous travaillons</h2>
            <p className="section-subtitle mt-4 max-w-2xl mx-auto">
              Un processus structuré pour garantir votre succès à chaque étape.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="relative" data-testid={`process-step-${index}`}>
                <div className="text-6xl font-bold text-[#006F33]/10 mb-4">{step.step}</div>
                <h3 className="text-xl font-semibold text-[#1A1A1A] mb-3">{step.title}</h3>
                <p className="text-slate-600 leading-relaxed">{step.description}</p>
                
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-8 right-0 translate-x-1/2">
                    <ArrowRight className="w-6 h-6 text-[#006F33]/30" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Audiences */}
      <section className="py-20 md:py-32 gradient-brand" data-testid="audiences-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-[#006F33] uppercase tracking-wider">Public Cible</span>
            <h2 className="section-title mt-4">À qui s'adressent nos services ?</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Entrepreneurs & Startups', desc: 'Porteurs de projets et jeunes entreprises en quête de structuration.' },
              { title: 'PME & TPE', desc: 'Entreprises établies cherchant à optimiser leur gestion et croissance.' },
              { title: 'Investisseurs', desc: 'Acteurs cherchant à comprendre le cadre juridique sénégalais.' },
              { title: 'Consultants Indépendants', desc: 'Professionnels souhaitant sécuriser leur activité juridiquement.' },
              { title: 'Institutions', desc: 'Organismes publics et associations impliqués dans le développement économique.' },
              { title: 'Étudiants & Diplômés', desc: 'Jeunes talents cherchant à lancer leur propre entreprise.' }
            ].map((audience, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm" data-testid={`audience-${index}`}>
                <h4 className="font-semibold text-[#1A1A1A] mb-2">{audience.title}</h4>
                <p className="text-slate-600 text-sm">{audience.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#006F33]" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Besoin d'un accompagnement ?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Contactez-nous pour une consultation gratuite et découvrez comment nous pouvons vous aider.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-[#006F33] hover:bg-slate-100 shadow-lg px-8 py-3 rounded-full font-medium" data-testid="services-cta-button">
              Prendre rendez-vous
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default ServicesPage;
